import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { getSuccessToast } from "../../../../common/infrastructure/utilities/toastUtility";
import {
  giftVoucherEmailFormDefaultValues,
  giftVoucherEmailFormSchema,
  IGiftVoucherEmailFormModel,
} from "../models/giftVoucherFormModel";
import {
  IGiftVoucherDetailsModel,
  ISendGiftVoucherEmailResultModel,
} from "../models/giftVoucherModels";

interface IUseGiftVoucherDetailsDialogProps {
  voucherId: number | null;
}

/**
 * Details for one voucher: who it is, what it is worth right now, and the full money
 * trail — every Issue / Spend / Refund row with the order it turned into.
 */
export const useGiftVoucherDetailsDialog = ({
  voucherId,
}: IUseGiftVoucherDetailsDialogProps) => {
  const [isCodeCopied, setIsCodeCopied] = useState(false);

  const { data, isLoading } = useApiQuery<IGiftVoucherDetailsModel>({
    queryKey: [endpointsMetadata.giftVoucher.getById, String(voucherId)],
    endpoint: `${endpointsMetadata.giftVoucher.getById}?id=${voucherId}`,
    enabled: !!voucherId,
  });

  const copyCode = useCallback(async () => {
    if (!data?.voucher.code) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.voucher.code);
      setIsCodeCopied(true);
      getSuccessToast("Кодът е копиран.");
      window.setTimeout(() => setIsCodeCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (insecure context, denied permission). The code
      // is printed in full on screen right next to the button, so there is nothing to
      // recover from — staying silent beats an error toast for a convenience action.
    }
  }, [data?.voucher.code]);

  // ─── Send by email ──────────────────────────────────────────────
  const emailForm = useForm<IGiftVoucherEmailFormModel>({
    defaultValues: giftVoucherEmailFormDefaultValues,
    resolver: yupResolver(giftVoucherEmailFormSchema),
    mode: "onChange",
  });

  // Seeded with the last address it went to, because re-sending to the same recipient
  // (a bounced mail, a lost inbox) is the common case.
  useEffect(() => {
    emailForm.reset({ email: data?.voucher.lastEmailSentTo ?? "" });
  }, [data?.voucher.lastEmailSentTo, emailForm]);

  const { mutate, isLoading: isSendingEmail } = useApiMutation();

  const sendEmail = emailForm.handleSubmit((values: IGiftVoucherEmailFormModel) => {
    if (!voucherId) {
      return;
    }

    mutate(
      {
        endpoint: endpointsMetadata.giftVoucher.sendEmail,
        method: "post",
        globalIsLoading: false,
        disableSuccessToast: true,
        payload: { id: voucherId, email: values.email },
        invalidateKeys: [
          [endpointsMetadata.giftVoucher.getById, String(voucherId)],
          [endpointsMetadata.giftVoucher.getAll],
        ],
      },
      {
        // The API still delivers the voucher when the PDF service is unavailable — the body
        // carries the code — so say plainly which of the two the recipient got.
        onSuccess: (result: ISendGiftVoucherEmailResultModel) =>
          getSuccessToast(
            result?.pdfAttached
              ? "Ваучерът е изпратен."
              : "Ваучерът е изпратен без PDF (услугата за PDF не е налична). Кодът е в самия имейл.",
          ),
      },
    );
  });

  return {
    voucher: data?.voucher,
    transactions: data?.transactions ?? [],
    isLoading,
    isCodeCopied,
    copyCode,
    emailForm,
    isSendingEmail,
    sendEmail,
  };
};
